
import type { TransactionApi, TransactionLineItemApi } from "@/types/api/transactions";
import type { Transaction, TransactionLineItem, TransactionType, TransactionStatus } from "@/types/transaction";
import { calculateTransactionStatus } from "@/lib/utils/transaction-status";

function mapTransactionTypeApiToUi(apiType: string): TransactionType {
  const typeMap: Record<string, TransactionType> = {
    "invoice": "invoice",
    "credit_note": "credit_note",
    "receipt": "receipt",
    "debit_adjustment": "debit_adjustment",
    "credit_adjustment": "credit_adjustment",
  };
  return typeMap[apiType.toLowerCase()] ?? "invoice";
}

// Note: Status is now calculated dynamically based on outstanding amount and due date
// This function is kept for backward compatibility but not used in the main mapper
function mapTransactionStatusApiToUi(apiStatus: string): TransactionStatus {
  const statusMap: Record<string, TransactionStatus> = {
    "outstanding": "outstanding",
    "completed": "completed",
    "overdue": "overdue",
  };
  return statusMap[apiStatus.toLowerCase()] ?? "outstanding";
}

function mapLineItemApiToUi(apiLineItem: TransactionLineItemApi): TransactionLineItem {
  return {
    id: apiLineItem.id,
    itemCode: apiLineItem.itemCode,
    description: apiLineItem.description,
    project: apiLineItem.project,
    projectName: apiLineItem.projectName,
    costCentre: apiLineItem.costCentre,
    costCentreName: apiLineItem.costCentreName,
    quantity: apiLineItem.quantity,
    unitPrice: apiLineItem.unitPrice,
    subtotal: apiLineItem.subtotal,
    taxAmount: apiLineItem.taxAmount,
    total: apiLineItem.total,
  };
}

export function mapTransactionApiToUi(apiTransaction: TransactionApi): Transaction {
  // Calculate status dynamically based on outstanding amount and due date
  const status = calculateTransactionStatus(
    apiTransaction.outstandingAmount,
    apiTransaction.dueDate
  );

  return {
    id: apiTransaction.id,
    referenceNumber: apiTransaction.referenceNumber,
    transactionDate: apiTransaction.transactionDate,
    customerId: apiTransaction.customerId,
    customerName: apiTransaction.customerName,
    transactionType: mapTransactionTypeApiToUi(apiTransaction.transactionType),
    amount: apiTransaction.amount,
    currency: apiTransaction.currency,
    status,
    description: apiTransaction.description ?? "",
    notes: apiTransaction.notes,
    createdBy: apiTransaction.createdBy ?? "",
    modifiedBy: apiTransaction.modifiedBy,
    createdAt: apiTransaction.createdAt,
    modifiedAt: apiTransaction.modifiedAt,
    dueDate: apiTransaction.dueDate,
    headerReference: apiTransaction.headerReference,
    paymentMethod: apiTransaction.paymentMethod,
    documentUrl: apiTransaction.documentUrl,
    outstandingAmount: apiTransaction.outstandingAmount,
    onDispute: apiTransaction.onDispute,
    accountCode: apiTransaction.accountCode,
    netAmount: apiTransaction.netAmount,
    vatAmount: apiTransaction.vatAmount,
    subLedger: apiTransaction.subLedger,
    orderNumber: apiTransaction.orderNumber,
    lineItems: apiTransaction.lineItems?.map(mapLineItemApiToUi),
  };
}
